import { CSSProperties, HTMLProps, ReactNode } from 'react';

import classNames from 'classnames';

import {
  TableCell as EncoreTableCell,
  Table,
  TableHeaderCell,
  TableRow,
  Text,
} from '@spotify-internal/encore-web';

import { ResourcePathArgs } from '../../hooks/useResourcePath';
import {
  StickyTableContainer,
  StickyTableHead,
  useStickyHeadOffset,
} from '../StickyTableHead';
import { TableSortLabel } from '../TableSortLabel';
import { ResourceTableRow } from './ResourceTableRow';

import styles from './ResourceTable.module.css';

const Root = ({
  children,
  ...props
}: Omit<HTMLProps<HTMLTableElement>, 'as'>) => (
  <Table {...props}>{children}</Table>
);

const Body = ({ children, ...props }: HTMLProps<HTMLTableSectionElement>) => (
  <tbody {...props}>{children}</tbody>
);

const HeaderRow = ({
  children,
  ...props
}: Omit<HTMLProps<HTMLTableRowElement>, 'as'>) => (
  <TableRow {...props}>{children}</TableRow>
);

type ResourceTableCellAlign = 'left' | 'center' | 'right';

type ResourceTableCellProps = Omit<
  HTMLProps<HTMLTableCellElement>,
  'align' | 'as'
> & {
  mediumScreen?: boolean;
  align?: ResourceTableCellAlign;
};

const encoreAlign: Record<ResourceTableCellAlign, 'start' | 'center' | 'end'> =
  {
    left: 'start',
    center: 'center',
    right: 'end',
  };

const withWidth = (
  width: ResourceTableCellProps['width'],
  style: ResourceTableCellProps['style'],
): CSSProperties | undefined =>
  width !== undefined ? { width, ...style } : style;

const HeaderCell = ({
  className,
  children,
  mediumScreen = false,
  align,
  width,
  style,
  ...props
}: ResourceTableCellProps) => {
  const topOffset = useStickyHeadOffset();

  return (
    <TableHeaderCell
      {...props}
      align={align && encoreAlign[align]}
      style={{ top: topOffset, ...withWidth(width, style) }}
      className={classNames(
        styles.stickyHeaderCell,
        styles.emphasized,
        { [styles.mediumScreenCell]: mediumScreen },
        className,
      )}
    >
      {children}
    </TableHeaderCell>
  );
};

const EmptyCell = () => (
  <Text variant="bodySmall" semanticColor="textSubdued">
    -
  </Text>
);

const isCellEmpty = (children: ReactNode): boolean =>
  children === null ||
  children === undefined ||
  typeof children === 'boolean' ||
  (typeof children === 'string' && children.trim() === '');

type CellProps = ResourceTableCellProps & {
  hidePlaceholder?: boolean;
  emphasized?: boolean;
};

const Cell = ({
  className,
  children,
  mediumScreen = false,
  hidePlaceholder = false,
  emphasized = false,
  align,
  width,
  style,
  ...props
}: CellProps) => (
  <EncoreTableCell
    {...props}
    align={align && encoreAlign[align]}
    style={withWidth(width, style)}
    className={classNames(
      {
        [styles.mediumScreenCell]: mediumScreen,
        [styles.emphasized]: emphasized,
      },
      className,
    )}
  >
    {!hidePlaceholder && isCellEmpty(children) ? <EmptyCell /> : children}
  </EncoreTableCell>
);

type RowProps = Omit<HTMLProps<HTMLTableRowElement>, 'name' | 'as'> &
  Partial<Pick<ResourcePathArgs, 'routeRef' | 'name' | 'params' | 'transform'>>;

const Row = ({ routeRef, name, params, transform, ...props }: RowProps) => {
  if (routeRef && name !== undefined) {
    return (
      <ResourceTableRow
        routeRef={routeRef}
        name={name}
        params={params}
        transform={transform}
        {...props}
      />
    );
  }
  return <TableRow {...props} />;
};

export const ResourceTable = {
  Root,
  Body,
  HeaderRow,
  HeaderCell,
  Cell,
  Row,
  StickyContainer: StickyTableContainer,
  StickyHead: StickyTableHead,
  SortLabel: TableSortLabel,
};
